import React, { useRef, useState, useEffect } from 'react';
import AISubmit from '../assets/AISubmit.svg';
import Arrow from '../assets/accordionIcon.svg';
import GlobeIcon from '../assets/globeIcon.svg';

import { useDispatch, useSelector } from 'react-redux';
import { handlePromptSearch } from './openAIUtils';
import Swal from 'sweetalert2';
import '@sweetalert2/theme-dark';
import {
    selectIsDropdownOpen,
    setIsDropdownOpen,
    setMainComponent,
    setPromptSource
} from '../store/slices/truPromptSlice';

const HandlePrompt = ({
    prompt,
    setPrompt,
    setLoading,
    selectedOptions,
    setSelectedOptions,
    openAIResults,
    setOpenAIResults,
}) => {
    const dispatch = useDispatch();
    const isDropdownOpen = useSelector(selectIsDropdownOpen);
    const dropdownRef = useRef(null);
    const [selectedLanguage, setSelectedLanguage] = useState(() => {
        try {
            const storedLanguage = localStorage.getItem('selectedLanguage');
            // Convert stored value to code if it's a full name
            if (storedLanguage === 'Hindi') return 'HN';
            if (storedLanguage === 'English') return 'EN';
            return storedLanguage || 'EN'; // Default to 'EN'
        } catch (error) {
            console.error('Error reading from localStorage:', error);
            return 'EN';
        }
    });
    const [isLangDropdownOpen, setLangDropdownOpen] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef(null);

    const optionsList = [
        { name: "SEO", param: "..." },
        { name: "Design", param: "..." },
        { name: "Communication", param: "..." },
        { name: "Web Development", param: "..." },
        { name: "Lead Generation", param: "..." },
        { name: "Lifecycle Marketing", param: "..." },
        { name: "Business Development", param: "..." },
    ];

    const toggleOption = (optionName) => {
        setSelectedOptions(prev =>
            prev.includes(optionName)
                ? prev.filter(name => name !== optionName)
                : [...prev, optionName]
        );
    };

    const getDropdownText = () => {
        if (selectedOptions.length === 0) return "Select Functions";
        if (selectedOptions.length === 1) return selectedOptions[0];
        return (
            <>
                <span>{selectedOptions[0]}</span><span className='functionsCount'>+{selectedOptions.length - 1} more</span>
            </>
        );
    };

    useEffect(() => {
        // Initialize speech recognition
        if ('webkitSpeechRecognition' in window) {
            recognitionRef.current = new window.webkitSpeechRecognition();
            recognitionRef.current.continuous = false;
            recognitionRef.current.interimResults = false;
            recognitionRef.current.lang = selectedLanguage === 'Hindi' ? 'hi-IN' : 'en-US';

            recognitionRef.current.onresult = (event) => {
                const transcript = event.results[0][0].transcript;
                setPrompt(transcript);
                setIsListening(false);
            };

            recognitionRef.current.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                setIsListening(false);
            };

            recognitionRef.current.onend = () => {
                setIsListening(false);
            };
        }

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, [selectedLanguage]);

    const toggleVoiceRecognition = () => {
        if (!recognitionRef.current) {
            Swal.fire({
                title: 'Not Supported',
                text: 'Speech recognition is not supported in your browser.',
                icon: 'error',
                confirmButtonText: 'OK',
                theme: 'dark'
            });
            return;
        }

        if (isListening) {
            recognitionRef.current.stop();
        } else {
            recognitionRef.current.start();
            setIsListening(true);
        }
    };

    // Function to handle language selection
    const handleLanguageSelect = (code) => {
        try {
            localStorage.setItem('selectedLanguage', code);
            setSelectedLanguage(code);
            setLangDropdownOpen(false);
        } catch (error) {
            console.error('Error saving language to localStorage:', error);
            setSelectedLanguage(code);
            setLangDropdownOpen(false);
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();

        if (selectedOptions.length === 0) {
            Swal.fire({
                title: 'Oops...!',
                text: 'Please select one of the options from the Dropdown.',
                icon: 'warning',
                confirmButtonText: 'OK',
                theme: 'dark'
            });
            return;
        }

        if (!selectedLanguage) {
            Swal.fire({
                title: 'Oops...!',
                text: 'Please select a language.',
                icon: 'warning',
                confirmButtonText: 'OK',
                theme: 'dark'
            });
            return;
        }

        const wordCount = prompt.trim().split(/\s+/).length;
        if (wordCount < 3) {
            Swal.fire({
                title: 'Oops...!',
                text: 'Ensure your search query includes a minimum of three words.',
                icon: 'warning',
                confirmButtonText: 'OK',
                theme: 'dark'
            });
            return;
        }

        try {
            handlePromptSearch(
                e,
                prompt,
                setPrompt,
                setLoading,
                setOpenAIResults,
                selectedOptions,
                setSelectedOptions,
                dispatch,
                selectedLanguage
            );
            dispatch(setMainComponent('promptResult'));
            dispatch(setPromptSource('openAIPrompts'));
        } catch (error) {
            Swal.fire({
                title: 'Oops...!',
                text: 'Error while getting the OpenAIResults...',
                icon: 'error',
                confirmButtonText: 'OK',
                theme: 'dark'
            });
        }
    };

    return (
        <form onSubmit={onSubmit} className="d-flex flex-sm-row flex-column userInputDiv">
            <div className="d-flex flex-row main-div">
                {/* Function Dropdown */}
                <div
                    className="dropdown selectFunctionsDiv d-flex justify-content-between"
                    ref={dropdownRef}
                >
                    <button
                        className="selectFunctionsBtn d-flex justify-content-between align-items-center"
                        type="button"
                        aria-expanded={isDropdownOpen}
                        onClick={() => {
                            dispatch(setIsDropdownOpen(!isDropdownOpen));
                        }}
                    >
                        <span className='selectFunctionsTitle d-flex align-items-center'>{getDropdownText()}</span>
                        <img
                            src={Arrow}
                            width={18}
                            height={18}
                            alt='Arrow'
                            className='selectFunctionsArrow'
                        />
                    </button>
                    {isDropdownOpen && (
                        <ul className="dropdown-menu show">
                            {optionsList.map((option) => (
                                <li key={option.name} onChange={() => toggleOption(option.name)}>
                                    <div className="dropdown-item">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`check-${option.name}`}
                                                checked={selectedOptions.includes(option.name)}
                                                role="button"
                                            />
                                            <label className="form-check-label" htmlFor={`check-${option.name}`} role="button">
                                                {option.name}
                                            </label>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Language Dropdown */}
                <div className="languageDropdown custom-language-dropdown d-flex me-2 position-relative">
                    <button
                        className="selectLanguageBtn d-flex align-items-center"
                        type="button"
                        onClick={() => setLangDropdownOpen(!isLangDropdownOpen)}
                    >
                        {selectedLanguage === '' ? (
                            <img
                                src={GlobeIcon}
                                width={20}
                                height={20}
                                alt="Language Selector"
                                className="globeIcon"
                            />
                        ) : (
                            <span className="d-flex align-items-center">
                                <span className="selectedLangCode me-1">
                                    {selectedLanguage}
                                </span>
                                <img
                                    src={Arrow}
                                    width={18}
                                    height={18}
                                    alt="Arrow"
                                    className="selectFunctionsArrow"
                                />
                            </span>
                        )}
                    </button>
                    {isLangDropdownOpen && (
                        <ul className="language-dropdown-menu show">
                            <li
                                className={`dropdown-item ${selectedLanguage === 'EN' ? 'active' : ''}`}
                                onClick={() => handleLanguageSelect('EN')}
                            >
                                English
                            </li>
                            <li
                                className={`dropdown-item ${selectedLanguage === 'HN' ? 'active' : ''}`}
                                onClick={() => handleLanguageSelect('HN')}
                            >
                                Hindi 
                            </li>
                        </ul>
                    )}
                </div>


            </div>

            {/* Prompt Input + Submit */}
            <div className="inputPrompt d-flex">
                <input
                    className="userInputField"
                    value={prompt}
                    placeholder="Message AI Prompt Generator"
                    onChange={(e) => setPrompt(e.target.value)}
                    onClick={() => dispatch(setIsDropdownOpen(false))}
                    style={{ flex: 1 }}
                />
                <button 
                    type="button"
                    className={`voiceButton ${isListening ? 'listening' : ''}`}
                    onClick={toggleVoiceRecognition}
                    title={isListening ? 'Stop listening' : 'Start voice input'}
                >
                    <i className={`fas ${isListening ? 'fa-stop' : 'fa-microphone'}`}></i>
                </button>
                <button className="AISubmit" type="submit" onClick={() => dispatch(setIsDropdownOpen(false))}>
                    <img
                        src={AISubmit}
                        width={24}
                        height={24}
                        alt='AI'
                    />
                </button>
            </div>
        </form>
    );
};

export default HandlePrompt;
